import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  markAllNotificationsRead,
  markNotificationRead,
  softDeleteNotification,
} from "@/features/notifications/notifications.api";
import type {
  MarkAllReadResponse,
  NotificationCategory,
  OkResponse,
} from "@/features/notifications/schemas/notification.schema";

/// Three small mutations sharing one invalidation pattern: every change
/// to a Notification row invalidates the inbox list AND the bell unread
/// count.
function invalidate(qc: ReturnType<typeof useQueryClient>, workspaceId: string | undefined) {
  void qc.invalidateQueries({ queryKey: ["notifications", workspaceId] });
}

export function useMarkNotificationRead(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<OkResponse, Error, string>({
    mutationFn: async (notificationId) => {
      if (!workspaceId) throw new Error("workspaceId required");
      return markNotificationRead(workspaceId, notificationId);
    },
    onSuccess: () => invalidate(qc, workspaceId),
  });
}

export function useMarkAllNotificationsRead(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<MarkAllReadResponse, Error, { category?: NotificationCategory } | undefined>({
    mutationFn: async (body) => {
      if (!workspaceId) throw new Error("workspaceId required");
      return markAllNotificationsRead(workspaceId, body ?? {});
    },
    onSuccess: () => invalidate(qc, workspaceId),
  });
}

export function useSoftDeleteNotification(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<OkResponse, Error, string>({
    mutationFn: async (notificationId) => {
      if (!workspaceId) throw new Error("workspaceId required");
      return softDeleteNotification(workspaceId, notificationId);
    },
    onSuccess: () => invalidate(qc, workspaceId),
  });
}
